/**
 * authProfile
 *
 * Server profile creation/fetch helpers, merging backend user data with
 * Firebase user and providing light caching.
 */
import { auth } from '../../firebase/firebase';
import { SERVER_URL } from '../../api/core';

// Cache expiration time in milliseconds (5 minutes)
const CACHE_EXPIRATION = 5 * 60 * 1000;

// Track the last fetch time and prevent concurrent fetches
let lastProfileFetchTime = 0;
let isFetchingProfile = false;

// Helper function to fetch enterprise data for enterprise users
const fetchEnterprisePermissions = async (userEmail, firebaseUser) => {
  if (!userEmail) return null;
  
  try {
    // For authenticated users, use the my-enterprise endpoint which includes permissions
    if (firebaseUser) {
      console.log('authProfile - Fetching enterprise data via my-enterprise endpoint');
      
      // Get auth token for authenticated request
      const token = await firebaseUser.getIdToken();
      
      const response = await fetch(`${SERVER_URL}/enterprise/my-enterprise`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.enterprise) {
          console.log('authProfile - Enterprise permissions fetched via my-enterprise:', data.enterprise);
          return {
            enterpriseData: data.enterprise,
            enterpriseName: data.enterprise.enterpriseName,
            enterpriseDomain: data.enterprise.enterpriseDomain,
            enterpriseImage: data.enterprise.enterpriseImageUrl || data.enterprise.enterpriseImage,
            enterpriseImagePath: data.enterprise.enterpriseImagePath
          };
        }
      } else {
        console.log('authProfile - my-enterprise endpoint failed, falling back to domain check');
      }
    }
    
    // Fallback to domain check for unauthenticated users or if my-enterprise fails
    const emailDomain = userEmail.split('@')[1];
    const response = await fetch(`${SERVER_URL}/enterprise/check-domain/${emailDomain}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {
      const data = await response.json();
      if (data.enterprise) {
        console.log('authProfile - Enterprise permissions fetched via domain check:', data.enterprise);
        return {
          enterpriseData: data.enterprise,
          enterpriseName: data.enterprise.enterpriseName,
          enterpriseDomain: data.enterprise.enterpriseDomain,
          enterpriseImage: data.enterprise.enterpriseImageUrl || data.enterprise.enterpriseImage,
          enterpriseImagePath: data.enterprise.enterpriseImagePath
        };
      }
    }
  } catch (error) {
    console.error('authProfile - Error fetching enterprise permissions:', error);
  }
  
  return null;
};

// Creates a new user profile on the server and returns the complete profile data
// UPDATED to accept and use the plan parameter for clearer behavior
export const createUserProfile = async (firebaseUser, firstName, lastName, plan = 'basic', extraProfile = null) => {
  try {
    console.log('Creating user profile for:', firebaseUser.uid, 'with plan:', plan);

    // Validate input parameters
    if (!firebaseUser || !firebaseUser.uid) {
      throw new Error('Invalid Firebase user object provided to createUserProfile');
    }

    if (!firstName || !lastName) {
      throw new Error('First name and last name are required to create a user profile');
    }

    // Get a fresh ID token for the request - retry up to 3 times if needed
    let idToken;
    let attempt = 0;
    const maxAttempts = 3;

    while (attempt < maxAttempts) {
      try {
        attempt++;
        console.log(`Attempt ${attempt} to get ID token for user ${firebaseUser.uid}`);
        idToken = await firebaseUser.getIdToken(true); // Force refresh token
        if (idToken) break;
      } catch (tokenError) {
        console.error(`Error getting ID token on attempt ${attempt}:`, tokenError);
        if (tokenError.code === 'auth/network-request-failed') {
           // Don't retry immediately on network errors, maybe wait longer or fail faster
           console.warn('Network error getting ID token, waiting longer before retry or failing...');
           await new Promise(r => setTimeout(r, 1500)); // Longer wait for network issues
        } else {
           await new Promise(r => setTimeout(r, 500)); // Short wait for other errors
        }
        if (attempt >= maxAttempts) throw new Error(`Failed to get ID token: ${tokenError.message}`); // Throw specific error after retries
      }
    }

    if (!idToken) {
      // This should theoretically not be reached if the loop above throws, but check just in case.
      throw new Error('Failed to get a valid ID token after multiple attempts');
    }

    console.log('Successfully obtained ID token. Length:', idToken?.length);

    // Create the request body with firstName, lastName, plan, and optional extras
    const requestBody = { 
      firstName, 
      lastName, 
      plan,
      ...(extraProfile ? {
        role: extraProfile.role || null,
        phone: extraProfile.phone || null,
        hasSeenContactInfoModal: extraProfile.hasSeenContactInfoModal
      } : {})
    };
    console.log('Request data for /auth/signup:', requestBody);

    // Call the auth/signup endpoint to create the user profile on the server
    // Add retry mechanism for network failures
    let apiResponse = null;
    let apiAttempt = 0;
    const maxApiAttempts = 2; // Max 2 retries (3 total attempts)
    
    while (apiAttempt < maxApiAttempts) {
      try {
        apiAttempt++;
        console.log(`API attempt ${apiAttempt} to create profile for user ${firebaseUser.uid}`);
        
        apiResponse = await fetch(`${SERVER_URL}/auth/signup`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          },
          // Send firstName, lastName and plan
          body: JSON.stringify(requestBody)
        });
        
        // Break out of retry loop if successful (or if we got a definitive error like 400)
        if (apiResponse.ok || apiResponse.status !== 503) {
          break;
        }
        
        // If we get a 503 (service unavailable), retry after a delay
        console.warn(`Server unavailable (503) on attempt ${apiAttempt}, will retry after delay`);
        await new Promise(r => setTimeout(r, 1000)); // 1 second delay before retry
      } catch (apiError) {
        console.error(`API network error on attempt ${apiAttempt}:`, apiError);
        if (apiAttempt >= maxApiAttempts) {
          throw new Error(`Failed to create profile after ${maxApiAttempts} attempts: ${apiError.message}`);
        }
        await new Promise(r => setTimeout(r, 1000)); // 1 second delay before retry
      }
    }
    
    if (!apiResponse) {
      throw new Error('Failed to get response from server after multiple attempts');
    }

    console.log(`Server response status for profile creation: ${apiResponse.status}`);

    // Enhanced error handling for response status
    if (!apiResponse.ok) {
      let errorMessage = `Server error: ${apiResponse.status}`;
      let errorData = null;
      try {
        errorData = await apiResponse.json();
        console.error('Server error response data:', errorData);
        // Use specific error from backend if available
        if (errorData && errorData.error) {
           errorMessage = errorData.error;
        }
      } catch (jsonError) {
        console.error('Failed to parse error response as JSON:', jsonError);
        // Try reading as text for non-JSON errors
        try {
          const textError = await apiResponse.text();
          console.error('Server error response text:', textError);
          if (textError) errorMessage = textError;
        } catch (textParseError) {
           console.error('Failed to read error response as text:', textParseError);
        }
      }

      // Create an error object with details
      const enhancedError = new Error(errorMessage);
      enhancedError.status = apiResponse.status;
      enhancedError.errorData = errorData; // Attach parsed data if available
      
      // Handle specific error codes from backend
      if (errorData && errorData.errorCode === 'EMAIL_ALREADY_EXISTS') {
        enhancedError.code = 'auth/email-already-in-use';
        enhancedError.errorCode = 'EMAIL_ALREADY_EXISTS';
      } else if (errorData && errorData.errorCode === 'PROFILE_ALREADY_EXISTS') {
        enhancedError.code = 'auth/profile-already-exists';
        enhancedError.errorCode = 'PROFILE_ALREADY_EXISTS';
      } else {
        enhancedError.code = 'auth/profile-creation-failed'; // Default code for profile creation errors
      }
      
      throw enhancedError; // Throw the enhanced error
    }

    // Parse success response data - this now contains the complete profile data
    let profileData;
    try {
      profileData = await apiResponse.json();
    } catch (jsonError) {
      console.error('Failed to parse success response as JSON:', jsonError);
      throw new Error('Invalid response format from server on success');
    }

    if (!profileData || typeof profileData !== 'object') { // Check if it's a valid object
      console.error('Empty or invalid profile data received from server:', profileData);
      throw new Error('Invalid or empty profile data returned from server');
    }

    console.log('Server response for profile creation (contains full profile):', profileData);

    // Extract the actual user data from the response
    const userData = profileData.user || profileData;
    
    // Merge the profile data returned from the backend with the firebaseUser object
    const mergedUser = {
      ...firebaseUser, // Base Firebase user info (like uid, emailVerified)
      ...userData,     // Profile data from backend (name, planLevel, subscriptionStatus, etc.)
      profileLastFetched: Date.now() // Add timestamp for cache management
    };

    // Ensure UID from firebaseUser isn't overwritten if backend sends it differently
    mergedUser.uid = firebaseUser.uid;
    
    // CRITICAL: Ensure planLevel is correctly set for new users
    // This prevents the infinite loading issue on the pricing page
    if (!mergedUser.planLevel || mergedUser.planLevel === 'standard') {
      mergedUser.planLevel = 'basic';
    }

    console.log('Returning merged user after profile creation:', mergedUser);
    return mergedUser;

  } catch (error) {
    // Log the error caught within this function's scope
    console.error('Error in createUserProfile function:', {
        message: error.message,
        status: error.status,
        errorData: error.errorData, // Log extra data if attached
        name: error.name,
        stack: error.stack?.split('\n').slice(0, 5).join('\n') // Log top of stack
    });

    // Ensure the error has the proper code for consistent handling
    if (!error.code) {
      error.code = 'auth/profile-creation-failed';
    }

    // Rethrow the error so the calling function can handle it
    throw error;
  }
};


// Fetches the user profile from the server and updates state via setUser
export const fetchUserProfile = async (firebaseUser, setUser, forceRefresh = false) => {
  // Check if firebaseUser is valid before proceeding
   if (!firebaseUser || !firebaseUser.uid || typeof firebaseUser.getIdToken !== 'function') {
       console.warn("authProfile - fetchUserProfile called without a valid firebaseUser object. Aborting.");
       // Depending on context, might return null or throw an error
       return null; // Or firebaseUser if it might be partially valid but unusable for fetch
   }

  const now = Date.now();
  // Prevent concurrent fetches EXCEPT during Google sign-in
  // Google sign-in needs to check if profile exists to decide whether to create one
  const googleSignInInProgress = sessionStorage.getItem('googleSignInInProgress') === 'true';
  if (isFetchingProfile && !googleSignInInProgress) {
    console.log("authProfile - Profile fetch already in progress, skipping duplicate request");
    // Return the potentially stale firebaseUser object currently in state
    return firebaseUser;
  }

  // Check cache validity
  if (!forceRefresh && firebaseUser?.profileLastFetched && (now - firebaseUser.profileLastFetched < CACHE_EXPIRATION)) {
     console.log("authProfile - Using cached profile data based on profileLastFetched timestamp");
     return firebaseUser; // Return the existing object with potentially cached profile data
  }
  // Fallback check using module-level timer if profileLastFetched isn't on user object yet
  if (!forceRefresh && !firebaseUser?.profileLastFetched && (now - lastProfileFetchTime < CACHE_EXPIRATION)) {
     console.log("authProfile - Using cached profile data based on last fetch time");
     return firebaseUser;
  }

  isFetchingProfile = true;
  console.log("authProfile - fetchUserProfile called for user:", firebaseUser.uid, "Force refresh:", forceRefresh);

  try {
    // Get token result which includes custom claims (like isAdmin)
    let tokenResult;
    let isAdminClaim = false;
    try {
      tokenResult = await firebaseUser.getIdTokenResult(false);
    } catch (tokenError) {
      // If current token fails, force refresh
      tokenResult = await firebaseUser.getIdTokenResult(true);
    }
    const idToken = tokenResult.token;
    isAdminClaim = tokenResult.claims?.isAdmin === true;

    const response = await fetch(`${SERVER_URL}/auth/profile`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json'
      }
    });

    console.log("authProfile - profile API response status:", response.status);

    if (!response.ok) {
      // Specific handling for 404 (Profile not found) - likely means signup wasn't completed server-side
      if (response.status === 404) {
        console.warn(`authProfile - Profile not found (404) for user ${firebaseUser.uid}.`);
        // Depending on app logic, you might want to trigger signup flow or sign out user
        const userNotFoundError = new Error('User profile not found on server.');
        userNotFoundError.code = 'auth/profile-not-found'; // Custom code
        userNotFoundError.status = 404;
        throw userNotFoundError; // Throw error to be handled by caller
      }
      // Handle other errors
      let errorMessage = `Server error fetching profile: ${response.status}`;
      try {
         const errorData = await response.json();
         if (errorData && errorData.error) errorMessage = errorData.error;
      } catch (e) { /* Ignore parse error */ }

      const error = new Error(errorMessage);
      error.status = response.status;
      throw error; // Throw error to be handled by caller
    }

    // Parse success response
    const profileData = await response.json();
    console.log("authProfile - profile data received:", profileData);

    if (!profileData || typeof profileData !== 'object') {
        console.error("authProfile - Invalid or empty profile data from server");
        throw new Error('Invalid profile data received');
    }

    // Check if user appears to be enterprise - ONLY by database flag, NOT by email domain
    // This prevents false positive enterprise detection for regular users with advocacylab.io emails
    const isEnterpriseUser = profileData.isEnterpriseUser === true || profileData.isEnterprise === true;
    
    // Fetch permissions if needed
    let enterprisePermissions = null;
    if (isEnterpriseUser && firebaseUser.email) {
      console.log('authProfile - Detected enterprise user, fetching permissions');
      enterprisePermissions = await fetchEnterprisePermissions(firebaseUser.email, firebaseUser);
    }

    // Update last fetch time (both module level and potentially on user object)
    const fetchTimestamp = Date.now();
    lastProfileFetchTime = fetchTimestamp;

    // Merge fetched profile data with existing firebaseUser object
    // Remove the manual subscription object creation
    const mergedUser = {
      ...firebaseUser,
      ...profileData,
      isAdmin: isAdminClaim, // Include admin claim from Firebase token
      profileLastFetched: fetchTimestamp // Add timestamp to user object for cache check
    };
    
    // Add enterprise permissions if available
    if (enterprisePermissions) {
      Object.assign(mergedUser, enterprisePermissions);
    }
    
    // Ensure UID consistency
    mergedUser.uid = firebaseUser.uid;

    console.log("authProfile - setting updated merged user with profile:", mergedUser);
    setUser(mergedUser); // Update state via the provided setter function
    return mergedUser; // Return the updated user object

  } catch (error) {
    console.error('authProfile - Error during profile fetch:', error.message, 'Status:', error.status, 'Code:', error.code);
    // Ensure fetching flag is cleared on error
    isFetchingProfile = false;

    // Handle specific errors or rethrow
    if (error.code === 'auth/profile-not-found') {
       // Decide how to handle profile not found - sign out? show message?
       // Rethrowing allows the caller (e.g., AuthContext) to decide
       throw error;
    }
     if (error.message?.includes('Failed to get ID token')) {
         // If token fetch failed, likely need to sign out or prompt re-login
         throw new Error(`Authentication error: ${error.message}`);
     }

    // For generic errors, maybe just log and return existing user data without updating state?
    // Or rethrow to let caller handle it. Rethrowing is generally better.
    console.warn("authProfile - Returning potentially stale user data due to fetch error.");
    // Don't update state with stale data on error, let caller handle the error.
    // setUser(firebaseUser); // Avoid this, might hide the error visually
    throw error; // Rethrow the error

  } finally {
    // Always clear the fetching flag
    isFetchingProfile = false;
  }
};

// Directly sets the user profile into state
// Useful after operations like plan changes that return the updated profile
export const setUserProfile = (profileData, setUser) => {
  console.log("authProfile - setUserProfile called with data:", profileData);
  const currentUser = auth.currentUser; // Get current Firebase user state

  if (currentUser && profileData) {
    // Merge provided profile data with the current Firebase user object
    // Remove manual subscription object creation
    const mergedUser = {
      ...currentUser, // Base Firebase user
      ...profileData,  // Updated profile fields from backend
      profileLastFetched: Date.now() // Update fetch timestamp
    };
    // Ensure UID consistency
    mergedUser.uid = currentUser.uid;

    console.log("authProfile - setting merged user directly:", mergedUser);
    setUser(mergedUser); // Update state

    // Update module-level cache time as well
    lastProfileFetchTime = mergedUser.profileLastFetched;
  } else {
    console.warn("authProfile - setUserProfile called but no current user or no profile data provided");
  }
};

